import React, { FC, useMemo } from 'react';
import classNames from 'classnames';
import Handlebars from 'handlebars';
import DOMPurify from 'dompurify';

import { listActionTypes } from '../../../../../../../../../constants';
import { Block } from 'apps/ng/src/app/services';
import { BlockControls } from './BlockControls';

interface Props {
  block: Block;
  context: any;
  css?: string;
  disabledActions: string[];
  onAction: (action: NgAction<Block>) => void;
  blockSchema?: any;
  zoomLevel?: number;
  viewportWidth?: number;
}

const blockItemStyles = `
/* CSS Reset and base styles for shadow DOM */
* {
  box-sizing: border-box;
}

:host {
  /* Base font settings that match typical browser defaults */
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  color: #000;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.block-item {
  position: relative;
  width: 100%;
  display: block;
  overflow: visible;
  margin: 0;
  transition: outline 0.3s ease-in-out, z-index 0.3s ease-in-out;
}

.min-height {
 min-height: 200px;
}

// reset default button styles
button {
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.block-item:hover {
  z-index: 3;
  outline: 2px solid blue;
}

.error-outline {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  outline: 2px solid red;
}

// show block controls on hover
.block-item:hover .block-controls-header {
  cursor: pointer;
  display: flex;
  z-index: 10;
}

.block-item.error {
  background-color: red;
}

.preview {
  width: 100%;
  min-height: 120px;
  overflow: visible;
  position: relative;
}

.error-message {
  color: red;
  margin: auto;
}

.error-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
`;

// ignore built-in helpers
const EXCLUDED_HELPERS = new Set(['if', 'else', 'each', 'with', 'unless', 'lookup', 'log', 'this', 'column', 'json']);

// parse handlebars AST and extract referenced keys with context awareness
const extractContextKeysWithContextTracking = (template: string): string[] => {
  const ast = Handlebars.parse(template);
  const keys = new Set<string>();
  const contextStack: { path: string; isArray: boolean }[] = [];

  function getFullPath(pathNode: any): string | null {
    if (!pathNode) return null;
    if (Array.isArray(pathNode.parts)) return pathNode.parts.join('.');
    if (typeof pathNode.original === 'string') return pathNode.original;
    return null;
  }

  function buildContextPrefix(): string {
    return contextStack
      .map(({ path, isArray }) => (isArray ? `${path}[]` : path))
      .filter(Boolean)
      .join('.');
  }

  function processHashParameters(hash: any): void {
    if (!hash || !hash.pairs) return;

    hash.pairs.forEach((pair: any) => {
      // only process path expressions
      if (pair.value && pair.value.type === 'PathExpression') {
        // skip meta refs like @index
        if (pair.value.original.startsWith('@')) return;

        const valuePath = getFullPath(pair.value);
        if (valuePath && !EXCLUDED_HELPERS.has(valuePath.split('.')[0])) {
          // Check if it's a relative key
          const isRelative = !valuePath.includes('.') && valuePath !== 'this';

          if (isRelative && contextStack.length > 0) {
            const contextPrefix = buildContextPrefix();
            const fullKey = contextPrefix ? `${contextPrefix}.${valuePath}` : valuePath;
            keys.add(fullKey);
          } else {
            keys.add(valuePath);
          }
        }
      }
    });
  }

  function visit(node: any): void {
    if (!node || typeof node !== 'object') return;

    if (node.type === 'PartialStatement') {
      // handle params in partial calls
      processHashParameters(node.hash);

      // Continue visiting other parts of the node
      for (const prop in node) {
        if (prop === 'hash') continue;
        const val = node[prop];
        if (Array.isArray(val)) {
          val.forEach(visit);
        } else if (val && typeof val === 'object' && val.type) {
          visit(val);
        }
      }
      return;
    }

    if (node.type === 'BlockStatement') {
      const blockPath = getFullPath(node.path);
      const isEach = blockPath === 'each';
      const isWith = blockPath === 'with';

      let isArray = false;
      let pushedPath = '';

      if (isEach || isWith) {
        if (node.params?.length > 0) {
          if (node.params[0].original.startsWith('@')) return;

          const paramPath = getFullPath(node.params[0]);
          if (paramPath) {
            isArray = isEach;
            pushedPath = paramPath;
            contextStack.push({ path: pushedPath, isArray });
          } else {
            contextStack.push({ path: '', isArray: false });
          }
        } else {
          contextStack.push({ path: '', isArray: false });
        }
      } else if (blockPath && !EXCLUDED_HELPERS.has(blockPath)) {
        contextStack.push({ path: blockPath, isArray: false });
      } else {
        contextStack.push({ path: '', isArray: false });
      }

      // Process hash parameters for block statements
      processHashParameters(node.hash);

      if (node.program) visit(node.program);
      if (node.inverse) visit(node.inverse);

      contextStack.pop();
      return;
    }

    if (node.type === 'MustacheStatement' || node.type === 'SubExpression') {
      const fullPath = getFullPath(node.path);
      if (!fullPath) return;

      if (EXCLUDED_HELPERS.has(fullPath.split('.')[0])) return;

      // Relative key: no dot or `this`
      const isRelative = !fullPath.includes('.') && fullPath !== 'this';

      if (isRelative && contextStack.length > 0) {
        const contextPrefix = buildContextPrefix();
        const fullKey = contextPrefix ? `${contextPrefix}.${fullPath}` : fullPath;
        keys.add(fullKey);
      } else {
        keys.add(fullPath);
      }

      // Process hash parameters for mustache statements
      processHashParameters(node.hash);
    }

    for (const prop in node) {
      const val = node[prop];
      if (Array.isArray(val)) {
        val.forEach(visit);
      } else if (val && typeof val === 'object' && val.type) {
        visit(val);
      }
    }
  }

  visit(ast);
  return [...keys];
};

// check if referenced paths actually exist in context
const findMissingContextKeys = ({ context, paths }: { context: any; paths: string[] }): string[] => {
  const missing: string[] = [];

  for (const path of paths) {
    const parts = path.split('.');
    let current = context;
    let exists = true;

    for (let part of parts) {
      const isArrayAccess = part.endsWith('[]');
      part = part.replace(/\[\]$/, '');

      if (current && typeof current === 'object' && Object.prototype.hasOwnProperty.call(current, part)) {
        current = current[part];

        if (isArrayAccess) {
          if (!Array.isArray(current)) {
            exists = false;
            break;
          }

          // check array structure with first element
          if (current.length > 0) {
            current = current[0];
          } else {
            exists = false;
            break;
          }
        }
      } else {
        exists = false;
        break;
      }
    }

    if (!exists) {
      missing.push(path);
    }
  }

  return missing;
};

export const PageLayoutBlockItem: FC<Props> = ({ block, context, disabledActions, onAction, blockSchema, zoomLevel = 100, viewportWidth }) => {
  const compiledBlock = useMemo(() => {
    try {
      if (!block.html) {
        return {
          classNames: 'min-height error-outline',
          html: '<div class="error-message">Error rendering preview. No Block html found!</div>',
        };
      }

      if (typeof block.html !== 'string') {
        return {
          classNames: 'min-height error-outline',
          html: '<div class="error-message">Error rendering preview. Invalid html provided!</div>',
        };
      }

      // Use block variables for scaffolded context if available, otherwise use provided context
      let templateContext = context || {};
      let systemData: any = {
        System: {
          BasePath: `/`,
          Pages: [],
          Page: {
            DisplayName: 'Preview',
            name: 'preview',
          },
          Sections: [{ DisplayName: 'Section #1' }, { DisplayName: 'Section #2' }, { DisplayName: 'Section #3' }],
          CurrentYear: new Date().getFullYear(),
        },
      };

      // If block has variables property, use it for scaffolding
      if (block.variables && typeof block.variables === 'object') {
        const blockVariables = block.variables as any;

        // Use Context from variables as template context
        if (blockVariables.Context) {
          templateContext = {
            ...blockVariables.Context,
            ...context, // Merge with actual context if provided
          };
        }

        // Use System/Project from variables for data
        if (blockVariables.System || blockVariables.Project) {
          systemData = {
            System: blockVariables.System || systemData.System,
            Project: blockVariables.Project || {},
          };
        }
      }

      const template = Handlebars.compile(block.html);
      const html = template(templateContext, { data: systemData });

      return {
        classNames: '',
        html: DOMPurify.sanitize(html),
      };
    } catch (err) {
      console.error('Handlebars compilation error:', err);
      return {
        classNames: 'min-height error-outline',
        html: '<div>Error rendering preview</div>',
      };
    }
  }, [block?.html, block?.variables, context]);

  return (
    <>
      <style>{blockItemStyles}</style>
      {block.blockStylesheet?.css && <style>{convertCssToShadowSafe(block.blockStylesheet.css)}</style>}

      <div key={block?.name} className={classNames('block-item', compiledBlock.classNames, { error: false })}>
        <div className="preview">
          <div className="preview-html" dangerouslySetInnerHTML={{ __html: compiledBlock.html }} />
        </div>

        <BlockControls
          title={blockSchema?.displayName || blockSchema?.title || block.displayName || block.name}
          disabledActions={disabledActions}
          onMoveUp={() => onAction({ type: listActionTypes.MOVE_UP, payload: block })}
          onMoveDown={() => onAction({ type: listActionTypes.MOVE_DOWN, payload: block })}
          onRemove={() => onAction({ type: listActionTypes.REMOVE, payload: block })}
          onSettings={() => onAction({ type: listActionTypes.OPEN_SETTINGS, payload: block })}
          zoomLevel={zoomLevel}
          viewportWidth={viewportWidth}
        />
      </div>
    </>
  );
};

// Helper to convert CSS for shadow DOM compatibility (same as HtmlTemplatePreview)
const convertCssToShadowSafe = (css?: string): string => {
  return typeof css === 'string'
    ? css
        .replace(/:root\b/g, ':host') // Replace :root with :host (word boundary to avoid partial matches)
        .replace(/position\s*:\s*sticky/gi, 'position: relative') // Sticky doesn't work well in shadow DOM
    : '';
};
